import { Button, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import GradientSuccessMarkIcon from "../../../assets/svgs/GradientSuccessMarkIcon";
import LoadingApp from "../../../components/LoadingApp";
import { BiometriaStatusEnum } from "../../../interfaces/enum/BiometriaStatusEnum";
import { CameraAcessProps } from "../../../interfaces/props/SaudeOnline/CameraAcessProps";
import { cadastrarBiometriaFacial, compararBiometriaFacial } from "../../../services/BiometriaFacial/BiometriaFacialService";
import ErrorIcon from "./ErrorIcon";
import './styles.css';
import { sxButton, sxTryAgainButton } from "./sxProps";

function CameraAcess(props: CameraAcessProps) {
    const [loading, setLoading] = useState<boolean>(false);
    const [biometriaStatus, setBiometriaStatus] = useState<BiometriaStatusEnum>(BiometriaStatusEnum.Capturando);
    const [message, setMessage] = useState<string>('');
    const [back, setBack] = useState<boolean>(false);
    const [count, setCount] = useState<number>(5);
    const [countErrors, setCountErros] = useState<number>(0);
    const [startCounting, setStartCounting] = useState<boolean>(false);
    const [registered, setRegistered] = useState<boolean>(false);
    const webcamRef = useRef<Webcam>(null);
    const landscapeMobile = useMediaQuery('(max-width: 1130px) and (max-height: 600px)');

    const { fontSizeStyleAccessibility } = useAccessibility();

    const pageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        pageRef?.current?.focus();
    }, []);

    useEffect(() => {
        if (!loading) {
            pageRef?.current?.focus();
        }
    }, [loading]);

    useEffect(() => {
        if (startCounting) {
            setTimeout(() => {
                if (count > 0) {
                    setCount(count - 1);
                }
            }, 1000);
        }
        if (count === 0) {
            props.handleValidation();
        }
    }, [count, startCounting]);

    const [devices, setDevices] = useState<CameraDeviceProps[]>([]);
    
    const handleDevices = useCallback(
      (mediaDevices: MediaDeviceInfo[]) =>
        setDevices(mediaDevices.filter(({ kind }) => kind === "videoinput")),
      []
    );
    
    useEffect(() => {
      navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, [handleDevices]);

    const capture = useCallback(() => {

        var width = window.innerWidth;
        var height = window.innerHeight;
        var imageSrc:any;

        if (height > width) {
            imageSrc= webcamRef?.current?.getScreenshot({width: 354, height: 472});
        } else {
            imageSrc= webcamRef?.current?.getScreenshot();
        }         

        if (imageSrc) {

            setLoading(true);

            cadastrarBiometriaFacial(imageSrc)?.then(response => {
                if (response?.result) {
                    const data = { ...response, result: JSON.parse(response?.result) };

                    if (data.result.error) {
                        setBiometriaStatus(BiometriaStatusEnum.Erro);
                        if (data.result.error === 'Identificador informado já possui cadastro na plataforma.') {
                            setBack(true);
                            setRegistered(true);
                        }
                        if (countErrors < 2) {
                            setMessage(data.result.error)
                        }
                        else {
                            setMessage('Houve uma falha com o sistema de autenticação de biometria facial, você poderá cadastrar sua biometria mais tarde.')
                            setBack(true);
                        }

                        setLoading(false);
                        return;
                    }

                    compararBiometriaFacial(imageSrc)?.then(response => {

                        if (response?.result === 'Beneficiário não reconhecido.') {
                            setBiometriaStatus(BiometriaStatusEnum.Erro);
                            setMessage(data.result.error)
                            setLoading(false);
                        }
                        setStartCounting(true)
                        setBiometriaStatus(BiometriaStatusEnum.Sucesso);
                        setLoading(false);
                    })

                }

            })

        }

    }, [webcamRef, countErrors]);

    const tryAgain = () => {
        if (countErrors < 2) {
            setCountErros(countErrors + 1)
        }
        else {
            setCountErros(0);
        }
        setBiometriaStatus(BiometriaStatusEnum.Capturando);
        setStartCounting(false);
        setCount(5);
    }

    const CaptureMessage = () => (
        <p className="camera-instructions" style={fontSizeStyleAccessibility} tabIndex={0}>
            Posicione seu rosto dentro da linha pontilhada, olhe para a câmera e clique no botão.
        </p>
    )


    return (

        <div className='camera-holder' tabIndex={-1} ref={pageRef}>
            {loading ? <LoadingApp text={'Analisando imagem, aguarde...'} showLoading={loading} /> :
                <>
                    <div className="camera-capture">
                        {biometriaStatus === BiometriaStatusEnum.Erro && !loading && 
                            <div className="camera-result-holder">
                                <div className="icon-holder">
                                    <ErrorIcon />
                                </div>
                                {!landscapeMobile &&
                                    <p className="camera-instructions" style={fontSizeStyleAccessibility}  aria-live="polite">
                                        Falha de comunicação
                                    </p>
                                }
                                {!landscapeMobile &&
                                    <p className="countdown" style={fontSizeStyleAccessibility} aria-live="polite">
                                        {message}
                                    </p>
                                }
                            </div>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Sucesso &&
                            <div className="camera-result-holder">
                                <div className="icon-holder">
                                    <GradientSuccessMarkIcon />
                                </div>
                                <p className="camera-instructions" style={fontSizeStyleAccessibility} tabIndex={0}>
                                    Biometria cadastrada com sucesso!
                                </p>
                                <p className="countdown" style={fontSizeStyleAccessibility} tabIndex={0}>
                                    (Essa tela será fechada em {count}s)
                                </p>
                            </div>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Capturando && <div className="face-orientation" />}
                        
                        {devices.length > 0 && <Webcam
                            ref={webcamRef}
                            mirrored={true}
                            audio={false}
                            height={'100%'}
                            width={'845px'}
                            className="camera"
                            screenshotFormat="image/jpeg"
                            videoConstraints={{
                                width: 1280,
                                height: 720,
                                facingMode: "user",
                                deviceId: devices[0].deviceId
                            }}
                        />}
                        {biometriaStatus === BiometriaStatusEnum.Capturando && !landscapeMobile &&
                            <CaptureMessage/>
                        }
                    </div>
                    <div className="camera-button">
                        {biometriaStatus === BiometriaStatusEnum.Capturando && landscapeMobile &&
                            <CaptureMessage/>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && !loading && landscapeMobile &&
                            <p className="camera-instructions" style={fontSizeStyleAccessibility} tabIndex={0}>
                                Falha de comunicação
                            </p>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && !loading && landscapeMobile &&
                            <p className="camera-instructions" style={fontSizeStyleAccessibility} tabIndex={0}>
                                {message}
                            </p>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Capturando &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={capture}
                                sx={sxButton}
                                style={fontSizeStyleAccessibility}
                                fullWidth>
                                CAPTURAR FOTO DE SEGURANÇA
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && !back &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={tryAgain}
                                sx={sxButton}
                                style={fontSizeStyleAccessibility}
                                fullWidth>
                                Tentar novamente
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && back &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={props.handleContinue}
                                sx={sxButton}
                                style={fontSizeStyleAccessibility}
                                fullWidth>
                                Retornar
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && back && !registered &&
                            <div className="try-again-button">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={tryAgain}
                                    sx={sxTryAgainButton}
                                    style={fontSizeStyleAccessibility}
                                    fullWidth>
                                    Tentar novamente
                                </Button>
                            </div>
                        }
                    </div>
                </>
            }

        </div>
    );
}

export default CameraAcess;